import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Wrench, Smartphone, Wifi, ThermometerSun, MoreHorizontal, ImagePlus, X, Loader2 } from "lucide-react";
import { generarTitulo } from "@/lib/titleGenerator";
import { fileToCompressedDataUrl } from "@/lib/imageUtils";

const CATS = [
    { v: "limpieza", l: "Limpieza", icon: Sparkles },
    { v: "mantenimiento", l: "Mantenimiento", icon: Wrench },
    { v: "app_foresta", l: "App Foresta", icon: Smartphone },
    { v: "internet", l: "Internet / WiFi", icon: Wifi },
    { v: "climatizacion", l: "Climatización", icon: ThermometerSun },
    { v: "otros", l: "Otros", icon: MoreHorizontal },
];

export default function ReportarIncidencia() {
    const nav = useNavigate();
    const fileInputRef = useRef(null);
    const [categoria, setCategoria] = useState(null);
    const [descripcion, setDescripcion] = useState("");
    const [fotos, setFotos] = useState([]);
    const [procesandoFoto, setProcesandoFoto] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!categoria) return toast.error("Elegí una categoría");
        if (!descripcion.trim()) return toast.error("Contanos qué pasó");
        const titulo = generarTitulo(categoria, descripcion);
        setBusy(true);
        try {
            await api.post("/incidencias", {
                titulo,
                descripcion: descripcion.trim(),
                categoria,
                prioridad: null,
                foto_urls: fotos,
            });
            toast.success("Reclamo enviado");
            nav("/mis-incidencias");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const addFoto = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset for next pick
        if (!file) return;
        setProcesandoFoto(true);
        try {
            const dataUrl = await fileToCompressedDataUrl(file);
            setFotos((prev) => [...prev, dataUrl]);
        } catch (err) {
            toast.error(err.message || "No se pudo procesar la imagen");
        } finally {
            setProcesandoFoto(false);
        }
    };

    return (
        <div className="max-w-xl">
            <h1 className="text-xl font-heading font-bold tracking-tight mb-3">Reportar Incidencia</h1>

            {/* Categorías */}
            <div className="grid grid-cols-3 gap-1.5 mb-3">
                {CATS.map((c) => {
                    const Icon = c.icon;
                    const active = categoria === c.v;
                    return (
                        <button
                            type="button"
                            key={c.v}
                            onClick={() => setCategoria(c.v)}
                            data-testid={`cat-${c.v}`}
                            className={`flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-md border transition-all ${
                                active
                                    ? "border-accent bg-accent/10 text-accent"
                                    : "border-border bg-card text-foreground hover:border-accent/40 hover:bg-accent/5"
                            }`}
                        >
                            <Icon className={`h-4 w-4 ${active ? "text-accent" : "text-muted-foreground"}`} />
                            <span className="text-[11px] font-medium leading-tight text-center">{c.l}</span>
                        </button>
                    );
                })}
            </div>

            {/* Descripción */}
            <div className="space-y-1.5 mb-3">
                <Label className="text-xs">Contanos qué pasó</Label>
                <Textarea
                    rows={4}
                    data-testid="inc-descripcion"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Ej: Hay una pérdida de agua debajo de la bacha"
                    className="resize-none min-h-[100px]"
                />
            </div>

            {/* Fotos */}
            <div className="space-y-1.5 mb-4">
                <Label className="text-xs">Foto (opcional)</Label>
                <p className="text-[11px] text-muted-foreground -mt-1">Podés sacar una foto o elegir una de la galería.</p>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={addFoto}
                    data-testid="inc-foto-input"
                />
                <div className="flex flex-wrap gap-2">
                    {fotos.map((f, i) => (
                        <div key={i} className="relative">
                            <img src={f} alt={`Foto ${i + 1}`} className="h-16 w-16 object-cover rounded-md border border-border" />
                            <button
                                type="button"
                                onClick={() => setFotos((prev) => prev.filter((_, j) => j !== i))}
                                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 shadow-sm"
                                aria-label="Quitar"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={procesandoFoto}
                        data-testid="inc-add-foto"
                        className="h-16 px-3 rounded-md border-2 border-dashed border-border text-muted-foreground flex items-center justify-center gap-1.5 hover:border-accent/40 hover:text-accent transition-colors disabled:opacity-50"
                    >
                        {procesandoFoto
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <><ImagePlus className="h-4 w-4" /><span className="text-xs font-medium">Agregar foto</span></>
                        }
                    </button>
                </div>
            </div>

            <Button
                onClick={submit}
                disabled={busy || !categoria || !descripcion.trim()}
                className="w-full sm:w-auto"
                data-testid="inc-submit"
            >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando…</> : "Reportar"}
            </Button>
        </div>
    );
}
